"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.assertValidRealm = exports.AuthenticationError = void 0;
const psl = require("psl");
const needle_1 = require("./needle");
class AuthenticationError extends Error {
    constructor(realmUrl, registryBase) {
        const message = `Unable to validate bearer realm '${realmUrl}' for registry '${registryBase}'. ` +
            `If you believe this registry is legitimate, you can submit a Support case ` +
            `via our portal at https://support.snyk.io or via email at support@snyk.io`;
        super(message);
        this.name = "AuthenticationError";
        this.realmUrl = realmUrl;
        this.registryBase = registryBase;
        Object.setPrototypeOf(this, new.target.prototype);
    }
}
exports.AuthenticationError = AuthenticationError;
// Allowlist for well-known registries whose auth endpoints are on a legitimately
// different registrable domain (not covered by the shared-registrable-domain check).
const REALM_ALLOWLIST = {
    "pkg.dev": ["oauth2.googleapis.com"],
    "azurecr.io": ["login.microsoftonline.com"], // Azure Container Registry
};
/**
 * Validates that a Bearer realm URL returned in a WWW-Authenticate header
 * targets the same host (or a closely related host) as the registry being accessed.
 *
 * This prevents a malicious registry from redirecting credential-bearing requests
 * to an attacker-controlled server.
 *
 * @param registryBase     - The registry hostname (with optional port), e.g. "registry-1.docker.io"
 *                           or "myregistry.azurecr.io". May include a port suffix.
 * @param realmUrl         - The full realm URL from the WWW-Authenticate header.
 * @param registryProtocol - The effective protocol used to contact the registry ("http:" or
 *                           "https:"). Defaults to "https:". When the registry is accessed over
 *                           HTTP, the realm is permitted to also use HTTP — hostname validation
 *                           remains the security control in that case.
 * @param reqOptions       - Optional request configuration options (ssrfProtectionDryRun, allowedHosts, disallowDangerousHosts)
 * @param observer         - Optional observer; used to record dry-run protection events.
 */
function assertValidRealm(registryBase, realmUrl, registryProtocol = "https:", reqOptions = {}, observer) {
    var _a;
    const deny = () => {
        if ((reqOptions === null || reqOptions === void 0 ? void 0 : reqOptions.ssrfProtectionDryRun) === true) {
            observer === null || observer === void 0 ? void 0 : observer({
                type: "SSRF_PROTECTION_DRY_RUN",
                payload: {
                    realmUrl,
                    registryBase,
                },
            });
            return;
        }
        throw new AuthenticationError(realmUrl, registryBase);
    };
    let parsedRealm;
    try {
        parsedRealm = new URL(realmUrl);
    }
    catch (_b) {
        deny();
        return;
    }
    let parsedRegistry;
    try {
        parsedRegistry = new URL(`${registryProtocol}//${registryBase}`);
    }
    catch (_c) {
        deny();
        return;
    }
    // Only allow HTTP if the registry is also HTTP
    const schemeOk = parsedRealm.protocol === "https:" ||
        (parsedRealm.protocol === "http:" && parsedRegistry.protocol === "http:");
    if (!schemeOk) {
        deny();
        return;
    }
    // registryBase may contain a port (e.g. "myregistry.com:5000"); strip it.
    const registryHost = parsedRegistry.hostname.toLowerCase();
    const realmHost = parsedRealm.hostname.toLowerCase();
    // Allow: realm host is in the allowedHosts list
    if ((reqOptions === null || reqOptions === void 0 ? void 0 : reqOptions.allowedHosts) &&
        (0, needle_1.allowIfAllowedHost)(realmHost, reqOptions.allowedHosts).allowed) {
        return;
    }
    // Deny: if disallowDangerousHosts is set and realm host is dangerous
    if ((reqOptions === null || reqOptions === void 0 ? void 0 : reqOptions.disallowDangerousHosts) &&
        !(0, needle_1.allowIfNotDangerousHost)(realmHost).allowed) {
        deny();
        return;
    }
    // Allow: exact match
    if (realmHost === registryHost) {
        return;
    }
    // Allow: realm is a subdomain of the registry host
    if (realmHost.endsWith(`.${registryHost}`)) {
        return;
    }
    // Allow: auth server is the parent domain of the registry host
    // (e.g. registry at us.gcr.io authenticates against gcr.io).
    // This cannot be exploited to exfiltrate credentials: to trigger this rule an
    // attacker would need to host a registry at a *subdomain* of the target realm
    // (e.g. evil.victim-auth.com), but credentials are sent to the realm host
    // (victim-auth.com), which the attacker does not control.
    if (registryHost.endsWith(`.${realmHost}`)) {
        return;
    }
    // Allow: both share the same registrable domain per the Public Suffix List
    // (e.g. auth.docker.io and registry-1.docker.io both map to docker.io).
    // psl.get() returns null for bare TLDs, IPs, and localhost — those fall through.
    const realmRegistrable = psl.get(realmHost);
    const registryRegistrable = psl.get(registryHost);
    if (realmRegistrable !== null &&
        registryRegistrable !== null &&
        realmRegistrable === registryRegistrable) {
        return;
    }
    // Allow: realm is in the per-registry allowlist for known cross-domain auth flows
    if (registryRegistrable !== null &&
        ((_a = REALM_ALLOWLIST[registryRegistrable]) === null || _a === void 0 ? void 0 : _a.includes(realmHost))) {
        return;
    }
    deny();
}
exports.assertValidRealm = assertValidRealm;
//# sourceMappingURL=realm-validation.js.map